import { HttpParams } from "@angular/common/http";
import { Inject, Injectable, forwardRef } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { firstValueFrom } from "rxjs";
import { DATA_EXPORTS_V2, DATA_EXPORT_STATUS, DATA_EXPORT_STATUS_BY_ID } from "../../common/endpoints";
import { DataExportStatus, PagedList, ProductModel, ProductModelPart, ThingInventoryManagementType } from "../../model";
import { AuthenticationService } from "../../service/authentication.service";
import { CustomLabelService } from "../../service/custom-label.service";
import { HttpService } from "../../service/http.service";
import { MetricService } from "../../service/metric.service";
import { ProductModelPartService } from "../../service/product-model-part.service";
import { ProductModelService } from "../../service/product-model.service";
import { ThingDefinitionService } from "../../service/thing-definition.service";
import { AbstractContextService } from "../../shared/class/abstract-context-service.class";
import { AbstractThingContextService } from "../../shared/class/abstract-thing-context-service.class";
import { LocalizationPipe } from "../../shared/pipe";
import { HttpUtility } from "../../utility";
import { QualifiedMetricId } from "./data-export-widget-schedule-page.component";

@Injectable()
export class DataExportWidgetService {

    private defaultFileName: string = "${thing.serialNumber}_${thing.name}";

    constructor(
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => HttpUtility)) private httpUtility: HttpUtility,
        @Inject(forwardRef(() => AbstractContextService)) private contextService: AbstractContextService,
        @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => ThingDefinitionService)) private thingDefinitionService: ThingDefinitionService,
        @Inject(forwardRef(() => ProductModelService)) private productModelService: ProductModelService,
        @Inject(forwardRef(() => ProductModelPartService)) private productModelPartService: ProductModelPartService,
        @Inject(forwardRef(() => MetricService)) private metricService: MetricService,
        @Inject(forwardRef(() => CustomLabelService)) private labelService: CustomLabelService,
        @Inject(forwardRef(() => MatSnackBar)) private snackBar: MatSnackBar,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe
    ) { }

    getPagedDataExportStatuses(sort: string[], pageIndex: number, pageSize: number, searchText: string): Promise<PagedList<DataExportStatus>> {
        const params = this.getParams(sort, pageIndex, pageSize, searchText);
        return firstValueFrom(this.httpService.get<PagedList<DataExportStatus>>(DATA_EXPORTS_V2, params));
    }

    getExportBulkDataById(id: string): Promise<DataExportStatus> {
        return firstValueFrom(this.httpService.get<DataExportStatus>(DATA_EXPORT_STATUS_BY_ID.replace('{id}', id)));
    }

    downloadFromExternalUrl(externalUrl: string, dateTime: number): void {
        const dateInput = dateTime.toString();
        const name = dateInput.substring(0, 8) + "-" + dateInput.substring(8);
        firstValueFrom(this.httpService.externalGet(externalUrl, { responseType: 'blob' })).then(blob => {
            this.httpUtility.wrapFileAndDownload({ file: blob, fileName: name + '.zip' });
        });
    }

    scheduleBulkDataExport(body: any): Promise<DataExportStatus> {
        return firstValueFrom(this.httpService.post<DataExportStatus>(DATA_EXPORT_STATUS, body));
    }

    deleteDataExportStatus(id: string): Promise<void> {
        return firstValueFrom(this.httpService.delete<void>(DATA_EXPORT_STATUS_BY_ID.replace('{id}', id)));
    }

    private getParams(sort: string[], pageIndex: number, pageSize: number, searchText: string): HttpParams {
        let params = new HttpParams();
        params = params.set('page', pageIndex + '');
        params = params.set('size', pageSize + '');
        if (sort && sort[0]) {
            params = params.set('sort', sort.join(','));
        }
        if (searchText) {
            params = params.set('searchText', searchText);
        }
        if (this.thingContextService.getCurrentThing()) {
            params = params.set('thingId', this.thingContextService.getCurrentThing().id);
        } else if (this.contextService.getCurrentLocation()) {
            params = params.set('locationId', this.contextService.getCurrentLocation().id);
        } else if (this.contextService.getCurrentCustomer()) {
            params = params.set('customerId', this.contextService.getCurrentCustomer().id);
        } else if (this.contextService.getCurrentPartner()) {
            params = params.set('partnerId', this.contextService.getCurrentPartner().id);
        }
        return params;
    }

    getRecursivelyAllDataExportStatus(page?: number, dataExportStatus?: DataExportStatus[]): Promise<DataExportStatus[]> {
        if (!page) {
            page = 0;
        }
        if (!dataExportStatus) {
            dataExportStatus = [];
        }
        return this.getPagedDataExportStatuses(['dateTime', 'desc'], page, 100, null)
            .then(pagedStatus => {
                dataExportStatus = dataExportStatus.concat(pagedStatus.content);
                if (pagedStatus.last) {
                    return dataExportStatus;
                } else {
                    return this.getRecursivelyAllDataExportStatus(++page, dataExportStatus);
                }
            });
    }

    getExportAllQualifiedMetricIds(params: HttpParams): Promise<QualifiedMetricId[]> {
        const metricParams = new HttpParams().set('userTypeId', this.authenticationService.getUser().userTypeId);
        if (this.authenticationService.getThingInventoryManagement() == ThingInventoryManagementType.BY_MODEL) {
            let promises = [];
            promises.push(this.productModelService.getProductModelsAssociatedToThings(params));
            promises.push(this.productModelPartService.getRecursivelyAllProductModelParts());
            return Promise.all(promises).then(results => {
                let productModels: ProductModel[] = results[0];
                const productModelIds = productModels.map(pm => { return pm.id });
                let productModelParts: ProductModelPart[] = results[1].filter((part: ProductModelPart) => part.thingDefinitionId && productModelIds.includes(part.productModelId));
                let metricPromises = [];
                productModels.forEach(pm => {
                    metricPromises.push(this.metricService.getMetricsByThingDefinitionId(pm.thingDefinitionId, true, metricParams).then(metrics => {
                        return metrics.map(m => { return { thingDefinitionId: null, productModelId: pm.id, productModelPartId: null, metricId: m.id, metricSetId: null } });
                    }));
                });
                productModelParts.forEach(part => {
                    metricPromises.push(this.metricService.getMetricsByThingDefinitionId(part.thingDefinitionId, true, metricParams).then(metrics => {
                        return metrics.map(m => { return { thingDefinitionId: null, productModelId: null, productModelPartId: part.id, metricId: m.id, metricSetId: null } });
                    }));
                });
                return Promise.all(metricPromises).then((results: QualifiedMetricId[][]) => {
                    return [].concat(...results);
                });
            });
        } else {
            return this.thingDefinitionService.getThingDefinitionsAssociatedToThings(params).then(thingDefinitions => {
                let promises = [];
                thingDefinitions.forEach(thingDef => {
                    promises.push(this.metricService.getMetricsByThingDefinitionId(thingDef.id, true, metricParams).then(metrics => {
                        return metrics.map(m => { return { thingDefinitionId: thingDef.id, productModelId: null, productModelPartId: null, metricId: m.id, metricSetId: null } });
                    }));
                });
                return Promise.all(promises).then((results: QualifiedMetricId[][]) => {
                    return [].concat(...results);
                });
            });
        }
    }

    showSnackbar(text: string, isError?: boolean): void {
        this.labelService.getCustomLabel(text)
            .then(message => {
                this.snackBar.open(this.localizationPipe.transform(message), '', {
                    duration: 4000,
                    panelClass: isError ? 'notification-error' : 'notification-info'
                });
            });
    }

    getDefaultFileName(): string {
        return this.defaultFileName;
    }

    getDefaultExportName(selectedPeriodString: string, isPartial: boolean): string {
        let nameParts: string[] = ["Export"];
        const currentThing = this.thingContextService.getCurrentThing();
        if (currentThing) {
            if (this.authenticationService.getThingInventoryManagement() == ThingInventoryManagementType.BY_MODEL) {
                if (currentThing.productModel) {
                    nameParts.push(currentThing.productModel.name);
                }
            } else {
                nameParts.push(currentThing.thingDefinition.name);
            }
            if (currentThing.serialNumber) {
                nameParts.push(currentThing.serialNumber);
            }
        }
        if (selectedPeriodString) {
            nameParts.push(selectedPeriodString);
        }
        if (isPartial) {
            nameParts.push("PARTIAL");
        }
        return nameParts.join("_");
    }

}